/**
 * Contracts describing the telemetry emitted by the PowerShell collectors —
 * independent ones, Copilot (AppInsightsClient.psm1 via
 * Invoke-CopilotTelemetryCollector.ps1), Claude Code
 * (Invoke-ClaudeCodeTelemetryCollector.ps1), and Codex
 * (Invoke-CodexTelemetryCollector.ps1), sharing the same
 * AppInsightsClient.psm1 but emitting distinct event names — see
 * ../../../collector/docs/ARCHITECTURE.md "The Claude Code collector" / "The
 * Codex collector". The adoption collectors land in the same Log Analytics workspace
 * behind Application Insights — since that resource is workspace-based, a
 * direct workspace query (what this API does, via
 * LogsQueryClient.queryWorkspace) hits the native `AppEvents` table, not the
 * classic `customEvents` compatibility view the Application Insights portal
 * shows. See `dashboard/api/src/queries/adoption.ts` /
 * `dashboard/api/src/queries/adoptionClaudeCode.ts` /
 * `dashboard/api/src/queries/adoptionCodex.ts` for the exact column mapping
 * (`Name`/`TimeGenerated`/`Properties`/`Measurements`).
 *
 * Copilot emits two custom event names per run:
 *  - `CopilotUsageSnapshot`: one aggregate event with allow-listed metric
 *    counts (from config `sanitization.allowedMetricNames`) as measurements,
 *    including source-specific VS Code and Copilot CLI session activity.
 *  - `CopilotUsageRecord`: zero or more per-usage-record events (currently
 *    VS Code only) with token counts as measurements.
 *
 * Claude Code emits the analogous pair:
 *  - `ClaudeCodeUsageSnapshot`: one aggregate event with
 *    `claude.code.requests`/`sessions`/`active.minutes` as measurements —
 *    also cumulative gauges over the lookback window, same as Copilot's.
 *  - `ClaudeCodeUsageRecord`: one event per assistant turn, with
 *    input/output/cache/thinking token counts as measurements, and a hashed
 *    `ProjectId`/`SessionId` (Copilot has neither) as properties.
 *
 * Codex emits a third analogous pair, with token semantics that differ from
 * both other tools — `inputTokens` is INCLUSIVE of `cachedInputTokens` (see
 * codexUsageRecordRowSchema below and ARCHITECTURE.md "The Codex
 * collector"):
 *  - `CodexUsageSnapshot`: one aggregate event with
 *    `codex.requests`/`sessions`/`active.minutes`/`subagent.requests` as
 *    measurements, plus optional rate-limit GAUGES (not cumulative
 *    counters — see ARCHITECTURE.md) when the collector's
 *    `includeRateLimits` config is enabled.
 *  - `CodexUsageRecord`: one event per turn, with
 *    input/cached-input/output/reasoning token counts as measurements, and
 *    hashed `ProjectId`/`SessionId`/`ThreadId` plus `Effort`/`IsSubagent` as
 *    properties.
 *
 * Kiro (AWS) emits a fourth pair. Kiro writes no token counts to disk, so its
 * token figures are ESTIMATES (visible text characters / 4) — never comparable
 * with, or summed into, the other tools' measured tokens (see
 * ../../../collector/docs/ARCHITECTURE.md "The Kiro collector"):
 *  - `KiroUsageSnapshot`: `kiro.requests`/`sessions`/`active.minutes`/
 *    `tool.calls` as measurements and `TokenBasis = estimated`.
 *  - `KiroUsageRecord`: one event per completed turn, with
 *    EstimatedInputTokens/EstimatedOutputTokens and tool-call counts as
 *    measurements, and hashed `ProjectId`/`SessionId` plus
 *    `Surface`/`AgentMode`/`Autopilot` as properties.
 *
 * All three `*UsageRecord` events additionally carry a raw `GitBranch`
 * property when (and only when) the collector's `sanitization.sendGitBranch`
 * opt-in is on — see ../../../collector/docs/PRIVACY.md "Git branch names".
 *
 * These names and field lists MUST stay in sync with the collectors. If a
 * collector's entry point changes what it sends, update this file and the
 * matching KQL query catalog in `dashboard/api/src/queries`.
 */
import { z } from 'zod';
export const COPILOT_USAGE_SNAPSHOT_EVENT = 'CopilotUsageSnapshot';
export const COPILOT_USAGE_RECORD_EVENT = 'CopilotUsageRecord';
/** One per VS Code chat or Copilot CLI session (collector 1.6.0+); re-sent when the session grows. */
export const COPILOT_SESSION_RECORD_EVENT = 'CopilotSessionRecord';
/**
 * One per Claude Code / Codex tool call (collector 1.6.0+), sent only with the
 * collector's `sanitization.sendToolNames` opt-in. Names only — never inputs or outputs.
 */
export const TOOL_CALL_RECORD_EVENT = 'ToolCallRecord';
export const CLAUDE_CODE_USAGE_SNAPSHOT_EVENT = 'ClaudeCodeUsageSnapshot';
export const CLAUDE_CODE_USAGE_RECORD_EVENT = 'ClaudeCodeUsageRecord';
export const CODEX_USAGE_SNAPSHOT_EVENT = 'CodexUsageSnapshot';
export const CODEX_USAGE_RECORD_EVENT = 'CodexUsageRecord';
export const KIRO_USAGE_SNAPSHOT_EVENT = 'KiroUsageSnapshot';
export const KIRO_USAGE_RECORD_EVENT = 'KiroUsageRecord';
export const GEMINI_USAGE_SNAPSHOT_EVENT = 'GeminiUsageSnapshot';
export const GEMINI_USAGE_RECORD_EVENT = 'GeminiUsageRecord';
export const CURSOR_USAGE_SNAPSHOT_EVENT = 'CursorUsageSnapshot';
export const CURSOR_USAGE_RECORD_EVENT = 'CursorUsageRecord';
export const WINDSURF_USAGE_SNAPSHOT_EVENT = 'WindsurfUsageSnapshot';
export const TOKEN_OPTIMIZATION_DAILY_EVENT = 'TokenOptimizationDaily';
export const TOKEN_OPTIMIZATION_SNAPSHOT_EVENT = 'TokenOptimizationSnapshot';
export const GRAPHIFY_BENCHMARK_SNAPSHOT_EVENT = 'GraphifyBenchmarkSnapshot';
/**
 * One privacy-safe health event per source per collector run (collector
 * 1.4.1+): whether the run worked and how much it handled, never what it
 * handled. See collector/src/Modules/CollectorHealth.psm1.
 */
export const COLLECTOR_RUN_EVENT = 'CollectorRun';
/** RTK's own estimated daily shell-output reduction; see collector/docs/ARCHITECTURE.md. */
export const RTK_OPTIMIZATION_METRIC_NAMES = {
    commands: 'optimization.rtk.commands',
    estimatedRawOutputTokens: 'optimization.rtk.estimated.raw.output.tokens',
    estimatedFilteredOutputTokens: 'optimization.rtk.estimated.filtered.output.tokens',
    estimatedSavedTokens: 'optimization.rtk.estimated.saved.tokens',
    reportedSavingsPercent: 'optimization.rtk.reported.savings.percent',
    /** Total wall-clock time of the day's RTK-wrapped commands (collector 1.2.0+). */
    execTimeMs: 'optimization.rtk.exec.time.ms',
};
/**
 * RTK's point-in-time snapshot (TokenOptimizationSnapshot, ToolName=rtk),
 * distinguished by the SnapshotKind property: `lifetime` (all-time totals
 * plus parse-failure recovery) or `command` (one allow-listed command family
 * from RTK's top-command table — see collector/docs/PRIVACY.md).
 */
export const RTK_SNAPSHOT_METRIC_NAMES = {
    lifetimeCommands: 'optimization.rtk.lifetime.commands',
    lifetimeRawOutputTokens: 'optimization.rtk.lifetime.raw.output.tokens',
    lifetimeFilteredOutputTokens: 'optimization.rtk.lifetime.filtered.output.tokens',
    lifetimeSavedTokens: 'optimization.rtk.lifetime.saved.tokens',
    lifetimeExecTimeMs: 'optimization.rtk.lifetime.exec.time.ms',
    parseFailures: 'optimization.rtk.parse.failures',
    parseRecoveryPercent: 'optimization.rtk.parse.recovery.percent',
    commandCount: 'optimization.rtk.command.count',
    commandSavedTokens: 'optimization.rtk.command.saved.tokens',
    commandSavingsPercent: 'optimization.rtk.command.savings.percent',
    commandAvgTimeMs: 'optimization.rtk.command.avg.time.ms',
};
export const HEADROOM_OPTIMIZATION_METRIC_NAMES = {
    tokensSaved: 'optimization.headroom.tokens.saved',
    tokensBefore: 'optimization.headroom.tokens.before',
    calls: 'optimization.headroom.calls',
    reportedSavingsPercent: 'optimization.headroom.reported.savings.percent',
};
export const GRAPHIFY_BENCHMARK_METRIC_NAMES = {
    benchmarkedRoots: 'optimization.graphify.benchmarked.roots',
    averageCorpusTokens: 'optimization.graphify.average.corpus.tokens',
    averageQueryTokens: 'optimization.graphify.average.query.tokens',
    averageEstimatedDeltaPerQuery: 'optimization.graphify.average.estimated.delta.per.query',
    averageReductionRatio: 'optimization.graphify.average.reduction.ratio',
    totalNodes: 'optimization.graphify.total.nodes',
    totalEdges: 'optimization.graphify.total.edges',
    /** Measured, not estimated: lifetime tokens Graphify reported spending to build the graphs (graphify-out/cost.json). */
    buildRoots: 'optimization.graphify.build.roots',
    buildInputTokens: 'optimization.graphify.build.input.tokens',
    buildOutputTokens: 'optimization.graphify.build.output.tokens',
    buildRuns: 'optimization.graphify.build.runs',
};
export const COPILOT_SESSION_METRIC_NAMES = {
    vscodeRequests: 'copilot.vscode.requests',
    vscodeSessions: 'copilot.vscode.sessions',
    vscodeActiveMinutes: 'copilot.vscode.active.minutes',
    cliRequests: 'copilot.cli.requests',
    cliSessions: 'copilot.cli.sessions',
    cliActiveMinutes: 'copilot.cli.active.minutes',
    visualStudioRequests: 'copilot.visualstudio.requests',
    visualStudioSessions: 'copilot.visualstudio.sessions',
    visualStudioActiveMinutes: 'copilot.visualstudio.active.minutes',
};
/** Metric names Copilot's collector is allowed to emit (config: sanitization.allowedMetricNames). */
export const KNOWN_METRIC_NAMES = [
    'copilot.completions.shown',
    'copilot.completions.accepted',
    'copilot.chat.requests',
    'copilot.chat.responses',
    'copilot.chat.requests.succeeded',
    'copilot.chat.requests.failed',
    'copilot.active.minutes',
    COPILOT_SESSION_METRIC_NAMES.vscodeRequests,
    COPILOT_SESSION_METRIC_NAMES.vscodeSessions,
    COPILOT_SESSION_METRIC_NAMES.vscodeActiveMinutes,
    COPILOT_SESSION_METRIC_NAMES.cliRequests,
    COPILOT_SESSION_METRIC_NAMES.cliSessions,
    COPILOT_SESSION_METRIC_NAMES.cliActiveMinutes,
    COPILOT_SESSION_METRIC_NAMES.visualStudioRequests,
    COPILOT_SESSION_METRIC_NAMES.visualStudioSessions,
    COPILOT_SESSION_METRIC_NAMES.visualStudioActiveMinutes,
];
/** Metric names the Claude Code collector is allowed to emit (same allowedMetricNames list). */
export const KNOWN_CLAUDE_METRIC_NAMES = [
    'claude.code.requests',
    'claude.code.sessions',
    'claude.code.active.minutes',
];
/**
 * Metric names the Codex collector is allowed to emit (same allowedMetricNames list). The
 * ratelimit.* names are point-in-time gauges, not cumulative counters — see
 * ../../../collector/docs/ARCHITECTURE.md "The Codex collector" — and are only emitted when
 * collection.agents.codex.includeRateLimits is enabled.
 */
export const KNOWN_CODEX_METRIC_NAMES = [
    'codex.requests',
    'codex.sessions',
    'codex.active.minutes',
    'codex.subagent.requests',
    'codex.ratelimit.primary.used.percent',
    'codex.ratelimit.secondary.used.percent',
];
/** Metric names the Kiro collector is allowed to emit (same allowedMetricNames list). */
export const KNOWN_KIRO_METRIC_NAMES = [
    'kiro.requests',
    'kiro.sessions',
    'kiro.active.minutes',
    'kiro.tool.calls',
];
/** customDimensions on a CopilotUsageSnapshot event. */
export const usageSnapshotDimensionsSchema = z.object({
    collectorVersion: z.string(),
    editorsScanned: z.string(), // comma-joined, e.g. "VSCode,VisualStudio"
    lookbackHours: z.string(),
    userId: z.string().optional(), // salted hash, present only when hashUserName is enabled
    osVersion: z.string().optional(),
});
/** customDimensions on a CopilotUsageRecord event. */
export const usageRecordDimensionsSchema = z.object({
    UserName: z.string(),
    Model: z.string(),
    /** Originating IDE label — "VS Code" | "Visual Studio" | "Unknown". Privacy-safe (never the local path); absent on records sent before this property existed. */
    Editor: z.string().optional(),
    editorsScanned: z.string(),
    lookbackHours: z.string(),
    UsageDate: z.string(), // ISO 8601 UTC, may be empty string
    PremiumQuotaUsage: z.string().optional(),
    /** Git branch name — present only when the collector opted in via sanitization.sendGitBranch (see PRIVACY.md). */
    GitBranch: z.string().optional(),
    /** Repository name (owner/repo) — present only when the collector opted in via sanitization.sendGitRepository (see PRIVACY.md). */
    GitRepository: z.string().optional(),
});
/**
 * One row as returned by a KQL projection over `customEvents` /
 * `customMeasurements` / `customDimensions` for a CopilotUsageSnapshot event.
 */
export const usageSnapshotRowSchema = z.object({
    timestamp: z.string(),
    cloudRoleInstance: z.string(), // hashed deviceId, blank when hashing disabled
    metricName: z.string(),
    metricValue: z.number(),
    editorsScanned: z.string(),
    userId: z.string().optional(),
});
/**
 * customDimensions on a CopilotSessionRecord event (collector 1.6.0+).
 * SessionId is a salted hash; customMeasurements carries `Requests` for the
 * whole session so far. The latest record per SessionId is the current one.
 */
export const copilotSessionRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    SessionId: z.string(),
    Surface: z.string(), // "VS Code" | "Copilot CLI"
    UserName: z.string(),
    Models: z.string(), // comma-joined model ids, may be empty
    FirstSeen: z.string(), // ISO 8601 UTC
    LastSeen: z.string(), // ISO 8601 UTC
});
/**
 * customDimensions on a ToolCallRecord event (collector 1.6.0+, opt-in).
 * SessionId and RecordId are salted hashes; customMeasurements carries `Calls` = 1.
 */
export const toolCallRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    Tool: z.enum(['claude-code', 'codex']),
    ToolName: z.string(),
    McpServer: z.string(), // '' for built-in tools
    SkillName: z.string(), // '' unless Claude Code's Skill tool
    SessionId: z.string(),
    UserName: z.string(),
    IsError: z.enum(['True', 'False', '']), // '' when the outcome is unknown
    IsRepeat: z.enum(['True', 'False']),
    UsageDate: z.string(), // ISO 8601 UTC
});
/**
 * One row as returned by a KQL projection over `customEvents` for a
 * CopilotUsageRecord event.
 */
export const usageRecordRowSchema = z.object({
    timestamp: z.string(),
    cloudRoleInstance: z.string(),
    model: z.string(),
    usageDate: z.string().optional(),
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    totalReasoningTokens: z.number().optional(),
});
/** customDimensions on a ClaudeCodeUsageSnapshot event. */
export const claudeCodeUsageSnapshotDimensionsSchema = z.object({
    collectorVersion: z.string(),
    lookbackHours: z.string(),
    userId: z.string().optional(), // salted hash, present only when hashUserName is enabled
    osVersion: z.string().optional(),
});
/**
 * customDimensions on a ClaudeCodeUsageRecord event. ProjectId and SessionId
 * are salted hashes (never raw paths; ProjectId is a project name instead
 * under the collector's sendRawProjectId opt-in) — see PRIVACY.md "How identifiers are
 * protected". There is no `File` field: unlike Copilot's fingerprint,
 * RecordId is derived from message.id/requestId, not a local file path.
 */
export const claudeCodeUsageRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    UserName: z.string(),
    Model: z.string(),
    ProjectId: z.string(), // salted 16-hex-char hash, or the project name with sanitization.sendRawProjectId
    SessionId: z.string(), // salted 16-hex-char hash of the local session key
    /** Collector 1.6.0+: "True" for subagent turns; absent on older records. */
    IsSubagent: z.string().optional(),
    /** Collector 1.6.0+: hash of the subagent run, or SessionId for root turns. */
    ThreadId: z.string().optional(),
    /** Collector 1.6.0+: "Claude Code", "Claude Desktop" or "Claude Desktop (Cowork)"; absent on older records. */
    Surface: z.string().optional(),
    UsageDate: z.string(), // ISO 8601 UTC
    /** Git branch name — present only when the collector opted in via sanitization.sendGitBranch (see PRIVACY.md). */
    GitBranch: z.string().optional(),
    /** Repository name (owner/repo) — present only when the collector opted in via sanitization.sendGitRepository (see PRIVACY.md). */
    GitRepository: z.string().optional(),
});
/**
 * One row as returned by a KQL projection over `AppEvents` for a
 * ClaudeCodeUsageSnapshot event.
 */
export const claudeCodeUsageSnapshotRowSchema = z.object({
    timestamp: z.string(),
    cloudRoleInstance: z.string(), // hashed deviceId, blank when hashing disabled
    metricName: z.string(),
    metricValue: z.number(),
    userId: z.string().optional(),
});
/**
 * One row as returned by a KQL projection over `AppEvents` for a
 * ClaudeCodeUsageRecord event.
 */
export const claudeCodeUsageRecordRowSchema = z.object({
    timestamp: z.string(),
    cloudRoleInstance: z.string(),
    model: z.string(),
    projectId: z.string().optional(),
    sessionId: z.string().optional(),
    usageDate: z.string().optional(),
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    cacheCreationTokens: z.number().optional(),
    cacheReadTokens: z.number().optional(),
    thinkingTokens: z.number().optional(),
});
/**
 * customDimensions on a CodexUsageSnapshot event. `planType` is present only
 * when collection.agents.codex.includeRateLimits is enabled — see
 * ../../../collector/docs/PRIVACY.md "Codex: what is collected".
 */
export const codexUsageSnapshotDimensionsSchema = z.object({
    collectorVersion: z.string(),
    lookbackHours: z.string(),
    userId: z.string().optional(),
    osVersion: z.string().optional(),
    planType: z.string().optional(),
});
/**
 * customDimensions on a CodexUsageRecord event. ProjectId/SessionId/ThreadId
 * are salted hashes (never raw paths/ids) — see PRIVACY.md "Codex: what is
 * collected". SessionId is always the root thread's hash; a subagent
 * thread's records share it and are distinguished by IsSubagent.
 */
export const codexUsageRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    UserName: z.string(),
    Model: z.string(),
    Effort: z.string(),
    Surface: z.string(), // session_meta.originator, e.g. "Codex Desktop" — low-cardinality, non-identifying
    ProjectId: z.string(),
    SessionId: z.string(),
    ThreadId: z.string(),
    IsSubagent: z.string(), // "True"/"False" — PowerShell's [string] cast of a bool
    UsageDate: z.string(),
    /** Git branch name — present only when the collector opted in via sanitization.sendGitBranch (see PRIVACY.md). */
    GitBranch: z.string().optional(),
    /** Repository name (owner/repo) — present only when the collector opted in via sanitization.sendGitRepository (see PRIVACY.md). */
    GitRepository: z.string().optional(),
});
/**
 * One row as returned by a KQL projection over `AppEvents` for a
 * CodexUsageSnapshot event.
 */
export const codexUsageSnapshotRowSchema = z.object({
    timestamp: z.string(),
    cloudRoleInstance: z.string(),
    metricName: z.string(),
    metricValue: z.number(),
    userId: z.string().optional(),
    planType: z.string().optional(),
});
/**
 * One row as returned by a KQL projection over `AppEvents` for a
 * CodexUsageRecord event. `inputTokens` is INCLUSIVE of `cachedInputTokens`
 * — see ../../../collector/docs/ARCHITECTURE.md "The Codex collector" for
 * why this differs from Claude Code's cache-token accounting. Billable
 * input tokens = inputTokens - cachedInputTokens.
 */
export const codexUsageRecordRowSchema = z.object({
    timestamp: z.string(),
    cloudRoleInstance: z.string(),
    model: z.string(),
    effort: z.string().optional(),
    surface: z.string().optional(),
    projectId: z.string().optional(),
    sessionId: z.string().optional(),
    threadId: z.string().optional(),
    isSubagent: z.boolean().optional(),
    usageDate: z.string().optional(),
    inputTokens: z.number().optional(),
    cachedInputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    reasoningTokens: z.number().optional(),
});
/** customDimensions on a KiroUsageSnapshot event. */
export const kiroUsageSnapshotDimensionsSchema = z.object({
    collectorVersion: z.string(),
    lookbackHours: z.string(),
    TokenBasis: z.literal('estimated'),
    userId: z.string().optional(),
    osVersion: z.string().optional(),
});
/**
 * customDimensions on a KiroUsageRecord event. ProjectId/SessionId are salted
 * hashes (never raw paths/ids) — see PRIVACY.md "Kiro: what is collected".
 */
export const kiroUsageRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    UserName: z.string(),
    Model: z.string(),
    Surface: z.string(), // "Kiro IDE" | "Kiro CLI"
    AgentMode: z.string(), // "vibe" | "spec" | "other" | "" (CLI)
    Autopilot: z.string(), // "True"/"False" — PowerShell's [string] cast of a bool
    ProjectId: z.string(),
    SessionId: z.string(),
    TokenBasis: z.literal('estimated'),
    UsageDate: z.string(),
    /** Repository name (owner/repo) — present only under sanitization.sendGitRepository (see PRIVACY.md). */
    GitRepository: z.string().optional(),
});
/**
 * customMeasurements on a KiroUsageRecord event. Token figures are ESTIMATES
 * (visible characters / 4) — never sum them with another tool's tokens.
 */
export const kiroUsageRecordMeasurementsSchema = z.object({
    EstimatedInputTokens: z.number(),
    EstimatedOutputTokens: z.number(),
    ToolCalls: z.number(),
    ToolErrors: z.number(),
    ShellToolCalls: z.number(),
    FilesystemToolCalls: z.number(),
    SearchToolCalls: z.number(),
    BrowserToolCalls: z.number(),
    RetrievalToolCalls: z.number(),
    OtherToolCalls: z.number(),
});
/**
 * customDimensions on a GeminiUsageRecord event (collector 1.6.0+).
 * ProjectId/SessionId are salted hashes — see PRIVACY.md "Gemini CLI: what is collected".
 */
export const geminiUsageRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    UserName: z.string(),
    Model: z.string(),
    Surface: z.literal('Gemini CLI'),
    ProjectId: z.string(),
    SessionId: z.string(),
    IsSubagent: z.string(), // "True"/"False"
    UsageDate: z.string(),
    GitRepository: z.string().optional(),
});
/**
 * customMeasurements on a GeminiUsageRecord event, in the Codex convention:
 * InputTokens INCLUDES CachedInputTokens (and tool-use prompt tokens);
 * OutputTokens INCLUDES ReasoningTokens (Gemini's thoughts).
 */
export const geminiUsageRecordMeasurementsSchema = z.object({
    InputTokens: z.number(),
    CachedInputTokens: z.number(),
    OutputTokens: z.number(),
    ReasoningTokens: z.number(),
});
/**
 * customDimensions on a CursorUsageRecord event (collector 1.6.0+): one completed
 * request. ProjectId/SessionId are salted hashes — see PRIVACY.md "Cursor: what is collected".
 */
export const cursorUsageRecordDimensionsSchema = z.object({
    RecordId: z.string(),
    UserName: z.string(),
    Model: z.string(),
    Surface: z.literal('Cursor IDE'),
    ProjectId: z.string(),
    SessionId: z.string(),
    TokenBasis: z.literal('reported-unverified'),
    UsageDate: z.string(),
});
/** customMeasurements on a CursorUsageRecord event. Tokens are Cursor's own tokenCount, not yet verified. */
export const cursorUsageRecordMeasurementsSchema = z.object({
    InputTokens: z.number(),
    OutputTokens: z.number(),
    ToolCalls: z.number(),
    ToolErrors: z.number(),
});
//# sourceMappingURL=telemetry.js.map